import React, { useCallback } from 'react'
import { useSelector } from 'react-redux'

import ModalForDisapprovalReason from '../ModalForDisapprovalReason'
import FileInstancesModal from '../../FileInstancesModal'
import { useFileInstancesModal } from '../hooks/useFileInstancesModal'

import useFileStatusUpdateToBE from '../hooks/useFileStatusUpdateToBE'
import { useDisapprovalModal } from '../hooks/useDisapprovalModal'

import { isControllerBuyerApprovalSelector } from '../../../../../../../modules/selectors/app'
import { mediaOrderRequestedValidFilesSelector } from '../../../../../../../modules/selectors/mediaOrdersBookings'

import { areUrlsEqual } from '../../FileInstancesModal/helpers'
import { DISAPPROVED_REASON } from '../../../../../../../features/components/Modals/AskReasonModal/fields'
import { getStatusForDisapprovalAction } from '../../../../../../../features/components/UploadedFilesField/helpers'

export const RequestedFilesContext = React.createContext()

export function RequestedFilesProvider({ children }) {
  // use only not floated - valid requests files
  const mediaOrderRequestedFiles = useSelector(mediaOrderRequestedValidFilesSelector)
  const isControllerBuyerApproval = useSelector(isControllerBuyerApprovalSelector)

  const { showFileInstancesModal, handleCloseFileInstancesModal, fileInstancesModalData, openFileInstancesModal } =
    useFileInstancesModal()

  const bulkUpdateStatusHandler = useFileStatusUpdateToBE()

  const handleStatusChange = useCallback(
    data => {
      const fileUrl = data.uploadedFile.file_url
      const allFileIds = mediaOrderRequestedFiles.flatMap(fileRequest =>
        fileRequest.uploaded_files.filter(file => areUrlsEqual(file.file_url, fileUrl)).map(file => file.id)
      )

      if (allFileIds.length > 1) {
        // If the user updates an uploaded file status (either using chip update method or Actions dropdown) and there is at
        // least one more uploaded file that has the same uploaded_file file_url in the same media order then prompt modal

        openFileInstancesModal({
          ...data,
          allFileIds
        })
      } else {
        // If there is only one uploaded file still use the bulk update method
        bulkUpdateStatusHandler({
          ids: [data.uploadedFile.id],
          status: data.status,
          disapprovedReason: data[DISAPPROVED_REASON]
        })
      }
    },
    [bulkUpdateStatusHandler, mediaOrderRequestedFiles, openFileInstancesModal]
  )

  const {
    handleShowDisapprovalModal,
    handleCloseDisapprovalModal,
    showDisapprovalReasonModal,
    selectedRequirementId,
    selectedUploadedFile
  } = useDisapprovalModal()

  const onDisapproveStatusChange = useCallback(
    ({ uploadedFile, [DISAPPROVED_REASON]: disapprovedReason }) => {
      const disapprovalActionStatus = getStatusForDisapprovalAction(uploadedFile, isControllerBuyerApproval)

      handleStatusChange({
        status: disapprovalActionStatus,
        uploadedFile,
        [DISAPPROVED_REASON]: disapprovedReason
      })
      handleCloseDisapprovalModal()
    },
    [handleStatusChange, handleCloseDisapprovalModal, isControllerBuyerApproval]
  )

  return (
    <RequestedFilesContext.Provider value={{ handleShowDisapprovalModal, handleStatusChange }}>
      {children}

      {showDisapprovalReasonModal && (
        // Don't render modal if it's not open, to reset the form on close
        <ModalForDisapprovalReason
          isOpen={showDisapprovalReasonModal}
          onClose={handleCloseDisapprovalModal}
          onSubmit={onDisapproveStatusChange}
          requirementFileId={selectedRequirementId}
          uploadedFile={selectedUploadedFile}
        />
      )}

      <FileInstancesModal
        isOpen={showFileInstancesModal}
        onFilesStatusChange={bulkUpdateStatusHandler}
        onClose={handleCloseFileInstancesModal}
        data={fileInstancesModalData}
      />
    </RequestedFilesContext.Provider>
  )
}
